import { Injectable } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RequestService } from 'src/app/core/service/request.service';
import { Transformation } from 'src/@utility/model-transformation';
import { UserGlobalSearchInterface } from '../core/interfaces/user-interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private restApi: RequestService) {}

  getGroupUsers$(groupId, transformFn = 'user') {
    const url = `/api/v5/groups/${groupId}/users`;
    return this.restApi
      .get(url, { page: 0, size: 2147483647 })
      .pipe(map((resp) => resp.body.map(Transformation[transformFn])));
  }

  getGroupsByType(type: string) {
    return this.restApi.get(`/api/v5/groups/type/${type}`, {
      page: 0,
      size: 2147483647,
    });
  }

  getUser(id: any) {
    return this.restApi.request('GET', `/api/v5/users/${id}`).pipe(
      catchError((error) => {
        return of({
          ...error,
          body: Transformation.deletedUser(id),
        });
      })
    );
  }

  createUser(data) {
    return this.restApi.request('POST', '/api/v5/users', data);
  }

  updateUser(id, data) {
    return this.restApi.request('PUT', `/api/v5/users/${id}`, data);
  }

  getUserNew(id: any) {
    return this.restApi.request('GET', `/api/v5/users/${id}`).pipe(
      catchError((error) => {
        return of({
          ...error,
          body: Transformation.deletedUser(id),
        });
      }),
      map((resp) => {
        return Transformation.user(resp.body);
      })
    );
  }

  getUserMultipleNew(userIds) {
    const processes = userIds.reduce((all, userId) => {
      all[userId] = this.getUserNew(userId);
      return all;
    }, {});

    return forkJoin(processes);
  }

  getUserMultiple(userIds) {
    const processes = userIds.reduce((all, userId) => {
      all[userId] = this.getUserNew(userId);
      return all;
    }, {});

    return forkJoin(processes);
  }

  addProfile(profile) {
    return this.restApi.request('POST', '/api/user-profiles', profile);
  }

  updateProfile(id, profile) {
    return this.restApi.request('PUT', '/api/user-profiles/' + id, profile);
  }

  getProfileByUserId(userId) {
    return this.restApi.get('/api/user-profiles', {
      'userId.equals': userId,
    });
  }

  updatePassword(data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/update-password/${data.id}`,
      data.password
    );
  }

  // use with caution
  getUsers(options = {}) {
    return this.restApi.get('/api/v5/users', { ...options });
  }

  getUserByAttributes(data = {}) {
    return this.restApi.request('GET', '/api/v5/user-by-attributes', {
      ...data,
    });
  }

  auditLogInfo(data: any) {
    return this.restApi.request('POST', '/api/v5/audit-logs', data);
  }

  getAllGroupOnlyOrgMembers(page = 0, size = 2147483647) {
    return this.restApi
      .request('GET', '/api/v5/groups-only-org-members', { page, size })
      .pipe(map((resp) => resp.body));
  }

  getAllGroupWithOutOrgMembers(page = 0, size = 2147483647) {
    return this.restApi
      .request('GET', '/api/v5/groups-without-org-members', { page, size })
      .pipe(map((resp) => resp.body));
  }

  sendEmails(data) {
    return this.restApi.request('POST', '/api/v5/send-mail', data);
  }

  findUsers(search?: string) {
    const options: UserGlobalSearchInterface = {
      page: 0,
      size: 2147483647,
      ...(search ? { search } : {}),
    };
    return this.restApi.request('GET', '/api/v5/find-users', options);
  }

  sendMail(userIds: string[], emailBody: any, subject?: any): void {
    const observables = userIds.map((id) => this.getUserNew(id));
    forkJoin(observables)
      .pipe(
        switchMap((users) => {
          const userDetails = users
            .filter((user) => !!user)
            .map((user) => ({
              id: user.id,
              email: user.email,
              name: user.firstName + ' ' + user.lastName,
              phoneNumber: user.attributes?.phoneNumber?.[0] ?? 0,
            }));
          const emailDto = userDetails.map((user) => ({
            email: user.email,
            phoneNumber: user.phoneNumber,
            language: 'en_US',
            msgBody: emailBody.replace('${userName}', user.name),
            subject: subject ? subject : 'AutoBCM',
          }));
          if (emailDto.length === 0) {
            return of([]);
          } else {
            return this.sendEmails(emailDto);
          }
        })
      )
      .subscribe();
  }

  // MIR3 APIs
  sendMir3Notification(data) {
    return this.restApi.request('POST', '/api/v5/sendNotification', data);
  }

  getNotificationReport(data) {
    return this.restApi.request(
      'POST',
      `/api/v5/getAllRecipientsNotificationReport`,
      data
    );
  }

  getReportDetails(reportType: any, data) {
    return this.restApi.request(
      'POST',
      `/api/v5/notification/${reportType}`,
      data
    );
  }

  terminateNotification(data) {
    return this.restApi.request('POST', `/api/v5/terminateNotification`, data);
  }

  getUserData(options = {}) {
    return this.restApi.request('GET', '/api/v5/app-key-user-stores', options);
  }

  // Everbridge APIs
  sendEbNotification(data) {
    return this.restApi.request('POST', '/api/v5/sendMassNotification', data);
  }

  getReport(niid) {
    return this.restApi.request('GET', `/api/v5/eb/report/${niid}`);
  }

  getNotificationStatus(id) {
    return this.restApi.request('GET', `/api/v5/eb/notificationStatus/${id}`);
  }

  terminateEbNotification(id) {
    return this.restApi.request('PUT', `/api/v5/eb/notification/${id}`);
  }
}
